import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Download, ShieldAlert, BellRing, BellOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { vouchersAPI } from '../../lib/api';

/** Rows from voucher_audit_events, inside the existing Voucher Settings audit
 *  tab. Deliberately NOT a second audit screen — it sits beside the scheduled
 *  report builder that already lives here.
 *
 *  Uses the shared Card + Table primitives rather than a hand-rolled <table>:
 *  a raw table inherited no themed text colour and rendered dark-on-dark,
 *  which made every cell except the badges invisible in dark mode. */

const DOWNLOAD_EVENTS = [
    'voucher_downloaded',
    'voucher_download_blocked',
    'voucher_spike_alert_sent',
    'voucher_spike_alert_failed',
].join(',');

const STYLES = {
    voucher_downloaded: { cls: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/40', Icon: Download },
    voucher_download_blocked: { cls: 'bg-red-500/15 text-red-500 border-red-500/40', Icon: ShieldAlert },
    voucher_spike_alert_sent: { cls: 'bg-amber-500/15 text-amber-500 border-amber-500/40', Icon: BellRing },
    voucher_spike_alert_failed: { cls: 'bg-slate-500/15 text-slate-400 border-slate-500/40', Icon: BellOff },
};

export const VoucherActivityLog = ({ organizationId }) => {
    const { t } = useTranslation();
    const [events, setEvents] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!organizationId) return;
        setBusy(true);
        try {
            const r = await vouchersAPI.giftAudit(organizationId, { event: DOWNLOAD_EVENTS });
            setEvents(r.data?.events || []);
        } catch { setEvents([]); }
        finally { setBusy(false); }
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    return (
        <Card data-testid="voucher-activity-log-panel">
            <CardHeader>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <CardTitle className="text-base flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4 text-emerald-500" />
                            {t('voucherAuditEvents.title', 'Voucher activity log')}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            {t('voucherAuditEvents.hint', 'Every download, blocked attempt and abuse alert, newest first.')}
                        </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="voucher-activity-refresh">
                        <RefreshCw size={14} className={`mr-1.5 ${busy ? 'animate-spin' : ''}`} />
                        {t('voucherAuditEvents.refresh', 'Refresh')}
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <div className="overflow-x-auto rounded-lg border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('voucherAuditEvents.colEvent', 'Event')}</TableHead>
                                <TableHead>{t('voucherAuditEvents.colCode', 'Voucher')}</TableHead>
                                <TableHead>{t('voucherAuditEvents.colEmail', 'Email')}</TableHead>
                                <TableHead>{t('voucherAuditEvents.colIp', 'IP address')}</TableHead>
                                <TableHead>{t('voucherAuditEvents.colWhen', 'When')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {events.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="h-24 text-center text-muted-foreground"
                                        data-testid="voucher-activity-empty">
                                        {t('voucherAuditEvents.empty', 'No voucher activity logged yet.')}
                                    </TableCell>
                                </TableRow>
                            ) : events.map((e) => {
                                const st = STYLES[e.event] || { cls: '', Icon: Download };
                                const Icon = st.Icon;
                                return (
                                    <TableRow key={e.id} data-testid={`voucher-activity-row-${e.id}`}>
                                        <TableCell>
                                            <Badge variant="outline" className={st.cls}>
                                                <Icon size={12} className="mr-1" />
                                                {t(`voucherAuditEvents.event.${e.event}`, e.event)}
                                            </Badge>
                                            {e.meta?.reason && (
                                                <span className="block text-[11px] text-muted-foreground mt-1">{e.meta.reason}</span>
                                            )}
                                            {e.meta?.channel && (
                                                <span className="block text-[11px] text-muted-foreground mt-1">
                                                    {e.meta.channel}{e.meta.recipient ? ` → ${e.meta.recipient}` : ''}
                                                    {e.meta.twilio_status ? ` (${e.meta.twilio_status})` : ''}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs text-foreground"
                                            data-testid={`voucher-activity-code-${e.id}`}>
                                            {e.voucher_code || '—'}
                                        </TableCell>
                                        <TableCell className="text-xs text-foreground break-all"
                                            data-testid={`voucher-activity-email-${e.id}`}>
                                            {e.meta?.email || '—'}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs text-foreground"
                                            data-testid={`voucher-activity-ip-${e.id}`}>
                                            {e.meta?.ip_address || '—'}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                            {e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
};

export default VoucherActivityLog;
